import React, { memo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { useRouter } from 'next/router';

import Typography from '../../atoms/typography';
import IconButton from '../../molecules/icon-button';
import IconCircleArrow from '../../atoms/icon-circle-arrow';
import Select from '../../atoms/select';

const NavTab = styled(Typography, { shouldForwardProp: (prop) => prop !== 'active' })`
  align-items: center;
  color: ${({ theme }) => theme.color.brand};
  cursor: pointer;
  display: flex;
  height: 5em;
  line-height: 1.5;
  margin: 0 1rem;
  padding: 0 1rem;
  text-align: center;
  background: transparent;
  border: none;
  letter-spacing: 0.05rem;
  transition: color ${({ theme }) => theme.animation.fast} ${({ theme }) => theme.animation.easeInQuad};

  && {
    font-size: 1.1rem;
  }

  :first-of-type {
    margin-left: 0;
  }

  :last-of-type {
    margin-right: 0;
  }

  :hover {
    color: ${({ theme }) => theme.color.brandLight};
  }

  ${({ active, theme }) => active && css`
    ${theme.arrow('up', theme.color.brandLight, 'bottom -1px center')}
    color: ${theme.color.brandLight};

    :hover {
      color: $theme.color.brandLight};
    }
  `}
`;

const Navigation = styled.nav`
  border-bottom: 1px solid ${({ theme }) => theme.color.brandLight};
  justify-content: center;
  margin-bottom: 5rem;
  display: none;

  @media (min-width: ${(props) => props.theme.breakpoint.medium}) {
    display: flex;
  }
`;

const MobileNavigation = styled(Select)`
  border: 0;
  display: block;
  margin: 3rem auto;
  max-width: 20rem;
  outline: 0;

  @media (min-width: ${(props) => props.theme.breakpoint.medium}) {
    display: none;
  }
`;

const Clothing = styled.div`
  margin: 0 auto;
  max-width: 40rem;
  padding: 3rem 0;
  position: relative;
  text-align: center;

  @media (min-width: ${(props) => props.theme.breakpoint.medium}) {
    display: flex;
    flex-direction: row;
    max-width: 100%;
  }

  @media (min-width: ${(props) => props.theme.breakpoint.medium}) {
    > figure {
      flex: 0 0 40rem;
      height: auto;
      order: 2;
      padding: 0;

      img {
        position: initial;
      }
    }
  }
`;

const ClothingTextChest = styled.div`
  color: ${(props) => props.theme.color.brand};
  flex: 1;
  margin-top: 4rem;
  padding: 3rem 0;
  text-align: center;

  > p {
    line-height: 1.5;
  }

  > label {
    display: block;
    letter-spacing: 0.2em;
    line-height: 2rem;
    margin-bottom: 1rem;
    text-transform: uppercase;
  }

  @media (min-width: ${(props) => props.theme.breakpoint.medium}) {
    order: 1;
    margin-top: 0;
  }
`;

const ClothingTextWaist = styled(ClothingTextChest)`
  margin-top: 0;

  @media (min-width: ${(props) => props.theme.breakpoint.medium}) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    order: 2;
  }
`;

const ShoesText = styled(Typography)`
  color: ${(props) => props.theme.color.brand};
  line-height: 1.5;
  margin: 0 auto;
  text-align: center;

  > button {
    margin: 1rem auto 0 auto;
    outline: 0;
    padding: 0;
    border: 0;

    svg {
      transform: rotate(90deg);
    }
  }
`;

const Shoes = styled.div`
  margin: 0 auto;
  max-width: 40rem;
  padding: 3rem 0;
  position: relative;
  text-align: center;
`;

const ImageWrapper = styled.figure`
  height: 0;
  margin-bottom: 3rem;
  overflow: hidden;
  padding-top: 100%;
  position: relative;

  > img {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
  }
`;

const SizeGuideMeasuring = (props) => {
  const [activeTab, setActiveTab] = useState(props.activeTab);
  const router = useRouter();
  const query = props.isPage ? router.query.selected : null;

  const updateQuery = (tab) => {
    if (props.isPage) {
      // persist the pageOnly query that displays
      // the page without the header, cart drawer and footer for iOS app
      const { pageOnly } = router.query;

      router.push({
        pathname: '/size-guide',
        query: { selected: tab, ...(pageOnly && { pageOnly }) }
      });
    }
  };

  const handleOnTabClick = (tab) => {
    setActiveTab(tab);
    updateQuery(tab);
  };

  const handleOnSelectChange = (e) => {
    setActiveTab(e.currentTarget.value);
    updateQuery(e.currentTarget.value);
  };

  const openShoePDF = () => {
    global.window.open(`${process.env.NEXT_PUBLIC_ASSET_HOST}/images/maisonette-shoe-chart.jpg`);
  };

  useEffect(() => {
    if (props.isPage && query) {
      setActiveTab(query);
    }
  }, [query]);

  return (
    <div>
      <Navigation>
        <NavTab
          active={activeTab === 'clothing' ? 'true' : undefined}
          element="button"
          like="dec-1"
          type="button"
          onClick={() => handleOnTabClick('clothing')}
        >
          Clothing
        </NavTab>

        <NavTab
          active={activeTab === 'shoes' ? 'true' : undefined}
          element="button"
          like="dec-1"
          type="button"
          onClick={() => handleOnTabClick('shoes')}
        >
          Shoes
        </NavTab>
      </Navigation>

      <MobileNavigation name="size-select-mobile" value={activeTab} onChange={(e) => handleOnSelectChange(e)}>
        <option value="clothing">Clothing</option>
        <option value="shoes">Shoes</option>
      </MobileNavigation>

      {
        activeTab === 'clothing'
          ? (
            <Clothing>
              <ImageWrapper>
                <img src={`${process.env.NEXT_PUBLIC_ASSET_HOST}/images/size-guide-clothing.jpg`} alt="Size Guide Shoes" />
              </ImageWrapper>

              <ClothingTextChest>
                <Typography element="label" like="label-1">Chest</Typography>
                <Typography element="p" like="paragraph-2">
                  Measure a loop just above your child’s armpits,
                  passing the tape across the fullest part of the
                  chest and around the shoulder blades.
                </Typography>
              </ClothingTextChest>

              <ClothingTextWaist>
                <Typography element="label" like="label-1">Waist</Typography>
                <Typography element="p" like="paragraph-2">
                  Measure a loop just above your child’s hip bone, around the natural waistline.
                </Typography>
              </ClothingTextWaist>
            </Clothing>
          )
          : (
            <Shoes>
              <ImageWrapper>
                <img src={`${process.env.NEXT_PUBLIC_ASSET_HOST}/images/size-guide-shoes.jpg`} alt="Size Guide Shoes" />
              </ImageWrapper>

              <ShoesText element="p" like="paragraph-2">
                Measure the bottom of your child’s foot,
                from the back of one foot to the tip of the longest toe.
                Measure both feet and use the longer of the two measurements.

                <IconButton
                  isText
                  text="Measuring Tool"
                  clicked={openShoePDF}
                >
                  <IconCircleArrow strokeWidth="4" />
                </IconButton>
              </ShoesText>
            </Shoes>
          )
      }
    </div>
  );
};

SizeGuideMeasuring.defaultProps = {
  activeTab: 'clothing',
  isPage: false
};

SizeGuideMeasuring.propTypes = {
  activeTab: PropTypes.string,
  isPage: PropTypes.bool
};

export default memo(SizeGuideMeasuring);
